import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import DialogContent from "@material-ui/core/DialogContent";
import styled from "styled-components/macro";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "../shared/Dialog";
import useModal from "../../hooks/useModal";
import { Grid, Typography } from "@material-ui/core";

const ImageContainer = styled(Grid)`
  justify-content: center;
  margin-bottom: 20px;
`;

const Image = styled.img`
  cursor: pointer;
  height: auto;
  max-width: 300px;
  max-height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid transparent;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #1976d2;
  }
`;

const DialogContentContainer = styled(DialogContent)`
  background: #f8f8f8;
  display: flex;
  flex-direction: column;
  min-height: 500px;
  max-height: 800px;
  overflow-y: auto;
  padding: 20px;
`;

const DialogTitleContainer = styled(DialogTitle)`
  background: #f8f8f8;
`;

const GalleryImagePreviewModal = ({ images, onConfirm, onClose }) => {
  const [loading, setLoading] = useState(true);
  const counter = useRef(0);

  const imagesLoaded = useCallback(() => {
    counter.current += 1;
    if (counter.current >= images.length) {
      setLoading(false);
    }
  }, [images.length]);

  const { handleConfirm } = useModal({
    onClose,
    onConfirm: useCallback((image) => onConfirm(image), [onConfirm]),
  });

  const getImageContainer = useCallback(
    (image) => {
      return (
        <ImageContainer
          key={image.id}
          container
          item
          alignItems="center"
          direction="column"
          lg={3}
          md={4}
          sm={6}
          xs={12}
        >
          <Image
            alt="Gallery image"
            src={image.url}
            onClick={() => handleConfirm(image)}
            onError={(e) => {
              e.target.style.display = "none";
              imagesLoaded();
            }}
            onLoad={imagesLoaded}
          />
        </ImageContainer>
      );
    },
    [handleConfirm, imagesLoaded]
  );

  const imagesGrid = useMemo(() => {
    if (!images || images.length === 0) {
      return (
        <Box
          alignItems="center"
          display="flex"
          height="200px"
          justifyContent="center"
        >
          <Typography color="textSecondary" variant="h6">
            No gallery images available
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {images.map((image) => getImageContainer(image))}
      </Grid>
    );
  }, [images, getImageContainer]);

  useEffect(() => {
    if (!images || images.length === 0) {
      setLoading(false);
    }
  }, [images]);

  return (
    <Dialog fullWidth open maxWidth="lg">
      <DialogTitleContainer>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h5">Gallery Images</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitleContainer>
      <DialogContentContainer>
        {loading && images && images.length > 0 && (
          <Box
            alignItems="center"
            display="flex"
            height="200px"
            justifyContent="center"
          >
            <CircularProgress size={40} />
          </Box>
        )}

        <Box style={{ display: loading ? "none" : "block" }}>{imagesGrid}</Box>
      </DialogContentContainer>
    </Dialog>
  );
};

export default GalleryImagePreviewModal;
